import { z } from "zod";

export const FEATURE_FLAG_GROUPS = [
  "developers",
  "test-group",
  "all-users",
] as const;

export const featureFlagGroupsSchema = z.array(z.enum(FEATURE_FLAG_GROUPS));

const featureFlagSchema = z.object({
  enabled: z.boolean().default(false),
  groups: featureFlagGroupsSchema.default(["developers", "test-group"]),
});

export const appConfigSchema = z.preprocess(
  (it) =>
    !it ? {} : typeof it === "string" && it.length > 0 ? JSON.parse(it) : it,
  z.object({
    dayPlanCancellationStatus: featureFlagSchema.default({
      enabled: true,
      groups: ["developers"],
    }),
    navRememberWhereUserWas: featureFlagSchema.default({
      enabled: true,
      groups: ["developers"],
    }),
    roleFromEntraGroup: featureFlagSchema.default({
      enabled: true,
      groups: ["developers"],
    }),
    pdfLayoutChanges: featureFlagSchema.default({
      enabled: true,
      groups: ["developers"],
    }),
    dayPlanChanges: featureFlagSchema.default({
      enabled: true,
      groups: ["developers"],
    }),
    sharepointDownloadUrl: featureFlagSchema.default({
      enabled: true,
      groups: ["developers"],
    }),
    drivingStyleLink: featureFlagSchema.default({
      enabled: true,
      groups: ["developers"],
    }),
    trainInfo_reasonCodes: featureFlagSchema.default({
      enabled: false,
      groups: ["developers"],
    }),
    resources_search: featureFlagSchema.default({
      enabled: false,
      groups: ["developers"],
    }),
    resources_cancelledTrains: featureFlagSchema.default({
      enabled: true,
      groups: ["developers"],
    }),
    checklist: featureFlagSchema.default({
      enabled: true,
      groups: ["developers"],
    }),
    efficiencyTips: featureFlagSchema.default({
      enabled: true,
      groups: ["developers"],
    }),
    stretchOverview: featureFlagSchema.default({
      enabled: true,
      groups: ["developers"],
    }),
    traininfo_enrichedPartialCancellationText: featureFlagSchema.default({
      enabled: true,
      groups: ["developers"],
    }),
    alternativeTransport: featureFlagSchema.default({
      enabled: true,
      groups: ["developers"],
    }),
    featureOnboarding: featureFlagSchema.default({
      enabled: true,
      groups: ["developers"],
    }),
    topasXL: featureFlagSchema.default({
      enabled: true,
      groups: ["developers"],
    }),
    sensorData: featureFlagSchema.default({
      enabled: true,
      groups: ["developers"],
    }),
    newRealtimeAndApc: featureFlagSchema.default({
      enabled: true,
      groups: ["developers"],
    }),
    expandedRosterActivitiesSelection: featureFlagSchema.default({
      enabled: true,
      groups: ["developers"],
    }),
    personnelRedesign: featureFlagSchema.default({
      enabled: true,
      groups: ["developers"],
    }),
    evaluationForm: featureFlagSchema.default({
      enabled: true,
      groups: ["developers"],
    }),
    syncAppConfig: featureFlagSchema
      .and(
        z.object({
          intervalMs: z.number(),
        }),
      )
      .default({
        enabled: true,
        groups: ["all-users"],
        intervalMs: 60_000,
      }),
  }),
);

export type AppConfig = z.infer<typeof appConfigSchema>;

type FeatureFlag = z.infer<typeof featureFlagSchema>;
export type FeatureFlagGroup = FeatureFlag["groups"][number];

type IsFeatureFlag<T> = T extends FeatureFlag ? T : never;

/** Extract all properties that confirms to featureFlagSchema.
 * For example: { a: { enabled: true, groups: []}, c: 1 } } => "a"
 * as c does not conform to the feature flag schema
 **/
type PathsToFeatureFlags<T extends object> = {
  [K in keyof T]: IsFeatureFlag<T[K]> extends never ? never : K;
}[keyof T];

// Our final FeatureFlagPath type that represents all possible keys of AppConfig, including nested ones
export type FeatureFlagPath = PathsToFeatureFlags<AppConfig>;

export function isFeatureFlag(obj: unknown): obj is FeatureFlag {
  return (
    obj !== null &&
    typeof obj === "object" &&
    "enabled" in obj &&
    "groups" in obj
  );
}

export const isFeatureFlagPath = (
  path: string,
  appConfig: AppConfig,
): path is FeatureFlagPath => {
  const featureFlags = Object.entries(appConfig).filter(([_, value]) =>
    isFeatureFlag(value),
  );

  return path in Object.fromEntries(featureFlags);
};
